/**
 * Sidebar component.
 * @module components/Sidebar/Sidebar
 */

import { useEffect, useState } from 'react';
import { Button, MenuItem, Menu } from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Cookies from 'universal-cookie';

import { Icon } from '@plone/volto/components';
import configurationSVG from '@plone/volto/icons/configuration.svg';
import logoSVG from 'volto-proseriestools/icons/logo.svg';

import { setCurrency } from '../../actions/currency/currency';

const CURRENCY_COOKIE = 'sidebar-currency';

// Reads the selected currency from its cookie (defaulting to USD when
// there's no stored value yet), tolerating cookies being disabled/missing.
const readCurrency = () => {
  try {
    const stored = new Cookies().get(CURRENCY_COOKIE);
    return stored === 'EUR' ? 'EUR' : 'USD';
  } catch (error) {
    return 'USD';
  }
};

/**
 * Sidebar component class.
 * @function Sidebar
 * @returns {string} Markup of the component.
 */
const Sidebar = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [showConfiguration, setShowConfiguration] = useState(false);
  const currency = useSelector((state) => state.currency);

  // Hydrate the shared (redux) currency from its cookie once on mount, so
  // it survives a reload but still lives in one place every view can read.
  useEffect(() => {
    dispatch(setCurrency(readCurrency()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      new Cookies().set(CURRENCY_COOKIE, currency, {
        path: '/',
        expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
      });
    } catch (error) {
      // Ignore (e.g. cookies disabled).
    }
  }, [currency]);

  const navTo = (path) => {
    history.push(path);
  };

  const isActive = (path) => location.pathname.includes(path);

  return (
    <div className="sidebar">
      <div
        className="button-toolbar sidebar-logo"
        role="button"
        tabIndex={0}
        onClick={() => navTo('/dashboard')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navTo('/dashboard');
          }
        }}
      >
        <Icon name={logoSVG} size="44px" />
      </div>
      <Menu secondary vertical fluid>
        <MenuItem
          name="orders"
          active={isActive('/orders')}
          onClick={() => navTo('/orders')}
        />
        <MenuItem
          name="contacts"
          active={isActive('/contacts')}
          onClick={() => navTo('/contacts')}
        />
        <MenuItem
          name="tools"
          active={isActive('/tools')}
          onClick={() => navTo('/tools')}
        />
        <MenuItem
          name="inventory"
          active={isActive('/inventory')}
          onClick={() => navTo('/inventory')}
        />
        <MenuItem
          name="backlog"
          active={isActive('/backlog')}
          onClick={() => navTo('/backlog')}
        />
        <MenuItem
          name="gift cards"
          active={isActive('/gift-cards')}
          onClick={() => navTo('/gift-cards')}
        />
        {showConfiguration && (
          <>
            <div className="separator" />
            <MenuItem
              name="categories"
              active={isActive('/categories')}
              onClick={() => navTo('/categories')}
            />
            <MenuItem
              name="blanks"
              active={isActive('/blanks')}
              onClick={() => navTo('/blanks')}
            />
            <MenuItem
              name="channels"
              active={isActive('/channels')}
              onClick={() => navTo('/channels')}
            />
          </>
        )}
      </Menu>
      <button
        type="button"
        className="circular-button sidebar-settings"
        onClick={() => setShowConfiguration(!showConfiguration)}
      >
        <Icon name={configurationSVG} size="24px" />
      </button>
      <div
        className="sidebar-currency"
        role="button"
        tabIndex={0}
        aria-label="Toggle currency"
        onClick={() =>
          dispatch(setCurrency(currency === 'USD' ? 'EUR' : 'USD'))
        }
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            dispatch(setCurrency(currency === 'USD' ? 'EUR' : 'USD'));
          }
        }}
      >
        <Button className={currency === 'USD' ? 'primary' : ''} as="span">
          USD
        </Button>
        <Button className={currency === 'EUR' ? 'primary' : ''} as="span">
          EUR
        </Button>
      </div>
    </div>
  );
};

export default Sidebar;
